import { Box } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { RHFRadioGroup } from 'src/components/hook-form';
import { translateKey } from 'src/locales/translate';
import { StockHandover } from 'src/sections/@dashboard/warehouse/utils/clinicalStock';
import MedicationStockRecipientFields from './MedicationStockRecipientFields';
import { SPAN_FULL } from './medicationFormLayout';
import { MedicationsFormModalValuesProps } from './medicationFormSchema';
import {
  StockHandoverChoice,
  StockPrescriptionChoice,
} from './medicationStockValues';

interface MedicationStockHandoverFieldsProps {
  locked: boolean;
  handover: StockHandoverChoice;
  /** The form's Rx flag, as the schema, the payload and the summary read it (P7B-R6). */
  isRx: boolean;
  /** A catalogue template names no recipient. */
  isTemplate: boolean;
  /** A new taking may start with the owner's address already on screen. */
  prefill: boolean;
  ownerAddress?: string;
}

const HANDOVER_OPTIONS = [
  {
    value: 'ADMINISTERED',
    label: translateKey('warehouse.clinical.handoverAdministered'),
  },
  {
    value: 'HANDED_OVER',
    label: translateKey('warehouse.clinical.handoverHandedOver'),
  },
];

const PRESCRIPTION_OPTIONS = [
  {
    value: 'ISSUED',
    label: translateKey('warehouse.clinical.prescriptionIssued'),
  },
  {
    value: 'NOT_ISSUED',
    label: translateKey('warehouse.clinical.prescriptionNotIssued'),
  },
];

/**
 * How the drug left the shelf, and for one handed over, its prescription
 * (an Rx drug) and recipient. Both answers go onto the Drug Record Card, so
 * neither is assumed where it cannot be (P7B-R9, P7B-R10). Each radio
 * replaces the field's onChange: the form validates on submit only, so an
 * answer after a refused "Dalej" clears its error at once.
 */
export default function MedicationStockHandoverFields({
  locked,
  handover,
  isRx,
  isTemplate,
  prefill,
  ownerAddress,
}: MedicationStockHandoverFieldsProps): React.ReactElement {
  const { setValue } = useFormContext<MedicationsFormModalValuesProps>();
  const answer = { shouldDirty: true, shouldValidate: true };

  return (
    <>
      <Box sx={SPAN_FULL}>
        <RHFRadioGroup
          row
          name="stockHandover"
          label={translateKey('warehouse.clinical.handover')}
          options={HANDOVER_OPTIONS}
          disabled={locked}
          onChange={(_event, value) =>
            setValue('stockHandover', value as StockHandover, answer)
          }
        />
      </Box>
      {handover === 'HANDED_OVER' && isRx && (
        <Box sx={SPAN_FULL} data-testid="medication-stock-prescription">
          <RHFRadioGroup
            row
            name="stockPrescription"
            label={translateKey('warehouse.clinical.prescription')}
            options={PRESCRIPTION_OPTIONS}
            disabled={locked}
            onChange={(_event, value) =>
              setValue(
                'stockPrescription',
                value as StockPrescriptionChoice,
                answer,
              )
            }
          />
        </Box>
      )}
      {handover === 'HANDED_OVER' && !isTemplate && (
        <MedicationStockRecipientFields
          locked={locked}
          isRx={isRx}
          prefill={prefill}
          ownerAddress={ownerAddress}
        />
      )}
    </>
  );
}
